import { DocumentChunk } from './chunkingService';

export interface EmbeddingResult {
  chunkId?: string;
  content: string;
  embedding: number[];
  metadata?: any;
}

export interface BatchEmbeddingResult {
  embeddings: EmbeddingResult[];
  totalTokens: number;
  model: string;
}

export class EmbeddingService {
  private static instance: EmbeddingService;
  private model = 'text-embedding-3-small'; // 1536 dimensions
  private maxBatchSize = 100; // OpenAI allows up to 2048 inputs per request
  private maxTokensPerBatch = 8000; // Conservative limit for token usage

  private constructor() {
    // Note: Embedding generation now handled by secure backend API
    // No API keys exposed to the browser
    console.log('🔐 EmbeddingService initialized - using secure backend API');
  }

  public static getInstance(): EmbeddingService {
    if (!EmbeddingService.instance) {
      EmbeddingService.instance = new EmbeddingService();
    }
    return EmbeddingService.instance;
  }

  /**
   * Generate embeddings for a single text
   */
  public async generateEmbedding(text: string): Promise<number[]> {
    try {
      // Clean and validate input
      const cleanedText = this.prepareText(text);

      if (!cleanedText) {
        throw new Error('Text is empty after cleaning');
      }

      // Call secure backend API endpoint
      const response = await fetch('/api/embeddings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          texts: [cleanedText],
          model: this.model
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || 'Failed to generate embedding');
      }

      const data = await response.json();
      return data.embeddings[0];
    } catch (error) {
      console.error('Error generating embedding:', error);
      throw new Error(`Failed to generate embedding: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate embeddings for multiple texts in batch
   */
  public async generateBatchEmbeddings(texts: string[]): Promise<BatchEmbeddingResult> {
    try {
      if (texts.length === 0) {
        return {
          embeddings: [],
          totalTokens: 0,
          model: this.model
        };
      }

      // Clean texts
      const cleanedTexts = texts.map(text => this.prepareText(text));

      // Process in batches to avoid API limits
      const batches = this.createBatches(cleanedTexts);
      const allEmbeddings: EmbeddingResult[] = [];
      let totalTokens = 0;

      for (const batch of batches) {
        // Call secure backend API endpoint
        const response = await fetch('/api/embeddings', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            texts: batch,
            model: this.model
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.details || 'Failed to generate batch embeddings');
        }

        const data = await response.json();

        // Map embeddings back to original texts
        for (let i = 0; i < data.embeddings.length; i++) {
          allEmbeddings.push({
            content: batch[i],
            embedding: data.embeddings[i],
          });
        }

        totalTokens += data.totalTokens || 0;
      }

      return {
        embeddings: allEmbeddings,
        totalTokens,
        model: this.model
      };
    } catch (error) {
      console.error('Error generating batch embeddings:', error);
      throw new Error(`Failed to generate batch embeddings: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate embeddings for document chunks
   */
  public async generateChunkEmbeddings(chunks: DocumentChunk[]): Promise<Map<number, number[]>> {
    try {
      const embeddingMap = new Map<number, number[]>();

      // Extract texts and maintain index mapping
      const texts = chunks.map(chunk => chunk.content);
      const result = await this.generateBatchEmbeddings(texts);

      // Map embeddings back to chunk indices
      chunks.forEach((chunk, index) => {
        if (result.embeddings[index]) {
          embeddingMap.set(chunk.metadata.chunkIndex, result.embeddings[index].embedding);
        }
      });

      console.log(`Generated embeddings for ${embeddingMap.size} chunks, used ${result.totalTokens} tokens`);
      return embeddingMap;
    } catch (error) {
      console.error('Error generating chunk embeddings:', error);
      throw error;
    }
  }

  /**
   * Generate embedding for a search query (may use different processing)
   */
  public async generateQueryEmbedding(query: string): Promise<number[]> {
    try {
      // For queries, we might want to add specific preprocessing
      const processedQuery = this.processSearchQuery(query);
      return await this.generateEmbedding(processedQuery);
    } catch (error) {
      console.error('Error generating query embedding:', error);
      throw error;
    }
  }

  /**
   * Use LangChain to generate embeddings (alternative method)
   */
  public async generateWithLangChain(texts: string[]): Promise<number[][]> {
    try {
      return await this.langchainEmbeddings.embedDocuments(texts);
    } catch (error) {
      console.error('Error generating embeddings with LangChain:', error);
      throw error;
    }
  }

  /**
   * Calculate cosine similarity between two embeddings
   */
  public calculateSimilarity(embedding1: number[], embedding2: number[]): number {
    if (embedding1.length !== embedding2.length) {
      throw new Error('Embeddings must have the same dimension');
    }

    let dotProduct = 0;
    let norm1 = 0;
    let norm2 = 0;

    for (let i = 0; i < embedding1.length; i++) {
      dotProduct += embedding1[i] * embedding2[i];
      norm1 += embedding1[i] * embedding1[i];
      norm2 += embedding2[i] * embedding2[i];
    }

    norm1 = Math.sqrt(norm1);
    norm2 = Math.sqrt(norm2);

    if (norm1 === 0 || norm2 === 0) {
      return 0;
    }

    return dotProduct / (norm1 * norm2);
  }

  /**
   * Find most similar embeddings from a set
   */
  public findMostSimilar(
    queryEmbedding: number[],
    embeddings: EmbeddingResult[],
    topK: number = 5,
    threshold?: number
  ): EmbeddingResult[] {
    // Calculate similarities
    const withSimilarity = embeddings.map(emb => ({
      ...emb,
      similarity: this.calculateSimilarity(queryEmbedding, emb.embedding)
    }));

    // Filter by threshold if provided
    let filtered = withSimilarity;
    if (threshold !== undefined) {
      filtered = withSimilarity.filter(item => item.similarity >= threshold);
    }

    // Sort by similarity and take top K
    return filtered
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, topK);
  }

  /**
   * Prepare text for embedding
   */
  private prepareText(text: string): string {
    // Remove excessive whitespace
    let cleaned = text.replace(/\s+/g, ' ').trim();

    // Remove null characters and other control characters
    cleaned = cleaned.replace(/[\x00-\x1F\x7F-\x9F]/g, '');

    // Limit length to avoid token limits (approximately 8000 tokens)
    const maxChars = 30000; // Conservative estimate
    if (cleaned.length > maxChars) {
      cleaned = cleaned.substring(0, maxChars) + '...';
    }

    return cleaned;
  }

  /**
   * Process search query for better embedding
   */
  private processSearchQuery(query: string): string {
    // Add context to improve search
    const processed = `Search query: ${query}`;
    return this.prepareText(processed);
  }

  /**
   * Create batches for API calls
   */
  private createBatches(texts: string[]): string[][] {
    const batches: string[][] = [];
    let currentBatch: string[] = [];
    let currentTokenEstimate = 0;

    for (const text of texts) {
      const tokenEstimate = Math.ceil(text.length / 4); // Rough estimate

      // Check if adding this text would exceed limits
      if (currentBatch.length >= this.maxBatchSize ||
          currentTokenEstimate + tokenEstimate > this.maxTokensPerBatch) {
        if (currentBatch.length > 0) {
          batches.push(currentBatch);
          currentBatch = [];
          currentTokenEstimate = 0;
        }
      }

      currentBatch.push(text);
      currentTokenEstimate += tokenEstimate;
    }

    // Add remaining batch
    if (currentBatch.length > 0) {
      batches.push(currentBatch);
    }

    return batches;
  }

  /**
   * Get embedding model information
   */
  public getModelInfo(): {
    model: string;
    dimensions: number;
    maxInputTokens: number;
  } {
    return {
      model: this.model,
      dimensions: 1536,
      maxInputTokens: 8191 // Model limit for text-embedding-3-small
    };
  }

  /**
   * Validate embedding dimension
   */
  public validateEmbeddingDimension(embedding: number[]): boolean {
    return embedding.length === 1536;
  }
}
